import { useState, useEffect, useCallback } from "react";
import axios, { getApiUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Search,
  Loader2,
  Check,
  X,
  AlertTriangle,
  Tag,
  Copy,
  ChevronDown,
  ChevronUp,
  Link2,
  Link2Off,
  GripVertical,
} from "lucide-react";
import {
  DndContext,
  useDraggable,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

const API = `${getApiUrl()}/api`;

// A draggable card for an unmatched source achievement. Shows name,
// description and tags so admins can eyeball a slightly-different title.
function DraggableSource({ source, tagMap, onPick }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `source-${source.sourceId}`,
  });
  const style = transform
    ? { transform: CSS.Translate.toString(transform), zIndex: 50 }
    : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-md p-2 text-xs bg-white/[0.03] border border-white/10 hover:border-cyan-400/40 transition ${
        isDragging ? "opacity-60 ring-1 ring-cyan-400/50" : ""
      }`}
      data-testid={`copy-tags-source-card-${source.sourceId}`}
    >
      <div className="flex items-start gap-1.5">
        <button
          {...listeners}
          {...attributes}
          className="mt-0.5 text-white/25 hover:text-cyan-400 cursor-grab active:cursor-grabbing shrink-0 touch-none"
          title="Drag onto a target achievement to link"
          data-testid={`copy-tags-source-drag-${source.sourceId}`}
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-white/80 font-medium truncate">{source.sourceName}</p>
            {source.sourceIsSecret && (
              <span className="text-[9px] px-1 py-0 rounded bg-amber-500/15 text-amber-300/80 border border-amber-400/20 font-semibold uppercase tracking-wide">
                Secret
              </span>
            )}
          </div>
          {source.sourceDescription && (
            <p className="text-white/30 text-[10px] mt-0.5 line-clamp-2">{source.sourceDescription}</p>
          )}
          <div className="mt-1 flex flex-wrap gap-1">
            {(source.sourceTags || []).map((tid) => (
              <Badge key={tid} variant="outline" className="text-[9px] px-1 py-0 h-4 border-white/10 text-white/40">
                {tagMap[tid]?.name || tid}
              </Badge>
            ))}
            {(!source.sourceTags || source.sourceTags.length === 0) && (
              <span className="text-white/20 text-[10px]">No tags</span>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPick(source)}
          className="h-6 px-2 text-[10px] shrink-0"
          data-testid={`copy-tags-source-link-${source.sourceId}`}
        >
          <Link2 className="w-3 h-3 mr-1" /> Link
        </Button>
      </div>
    </div>
  );
}

// A drop zone wrapping an unmatched target row. Uses a render-prop so the row
// can style itself while the source is hovering over it.
function DroppableTarget({ idx, children }) {
  const { isOver, setNodeRef } = useDroppable({ id: `target-${idx}` });
  return <div ref={setNodeRef}>{children(isOver)}</div>;
}

export function CopyTagsDialog({ open, onOpenChange, targetGameId, targetGameName, allTags, onApplied }) {
  const [step, setStep] = useState("search"); // search | preview | applying
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  // editingKey uses string identifiers so we can edit either matched or
  // unmatched-target rows: "matched-<idx>" or "unmatched-<idx>".
  const [editingKey, setEditingKey] = useState(null);
  const [editTags, setEditTags] = useState([]);
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  // Index of the unmatched-target row whose "Link source" picker is open.
  const [linkPickerIdx, setLinkPickerIdx] = useState(null);
  // Whether to copy the source's Secret flag onto matching target achievements.
  // Default on per user request; can be unchecked to keep tag-only copy semantics.
  const [includeSecret, setIncludeSecret] = useState(true);
  const [includeDlc, setIncludeDlc] = useState(false);

  // Tag lookup
  const tagMap = {};
  (allTags || []).forEach((t) => {
    tagMap[t.id] = t;
  });

  useEffect(() => {
    if (!open) {
      setStep("search");
      setSearchQuery("");
      setSearchResults([]);
      setSelectedSource(null);
      setPreview(null);
      setEditingKey(null);
      setApplyResult(null);
      setShowUnmatched(false);
      setLinkPickerIdx(null);
    }
  }, [open]);

  const searchGames = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const { data } = await axios.get(`${API}/master/games?search=${encodeURIComponent(q)}&limit=15`);
      // Filter out the target game itself
      setSearchResults((data.games || []).filter((g) => g.id !== targetGameId));
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [targetGameId]);

  useEffect(() => {
    const timer = setTimeout(() => searchGames(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchGames]);

  const loadPreview = async (sourceGame) => {
    setSelectedSource(sourceGame);
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/master/games/${targetGameId}/copy-tags-preview/${sourceGame.id}`
      );
      setPreview(data);
      setStep("preview");
    } catch (err) {
      alert("Failed to load preview: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const startEditRow = (type, idx) => {
    setEditingKey(`${type}-${idx}`);
    if (type === "matched") {
      setEditTags([...(preview.matched[idx].mergedTags || [])]);
    } else {
      // Unmatched target: seed edit tags from the existing target tags (or the
      // pending edits if already modified).
      const row = preview.unmatchedTarget[idx];
      setEditTags([...(row.pendingTags ?? row.targetExistingTags ?? [])]);
    }
  };

  const toggleEditTag = (tagId) => {
    setEditTags((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId]
    );
  };

  const saveEditRow = () => {
    if (editingKey === null) return;
    const [type, idxStr] = editingKey.split("-");
    const idx = parseInt(idxStr, 10);
    const updated = { ...preview };
    if (type === "matched") {
      updated.matched = [...updated.matched];
      updated.matched[idx] = { ...updated.matched[idx], mergedTags: [...editTags] };
    } else {
      updated.unmatchedTarget = [...updated.unmatchedTarget];
      updated.unmatchedTarget[idx] = {
        ...updated.unmatchedTarget[idx],
        pendingTags: [...editTags],
        edited: true,
      };
    }
    setPreview(updated);
    setEditingKey(null);
    setEditTags([]);
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  // Link an unmatched source achievement to an unmatched target row. Merges the
  // source's tags into the target's existing tags and captures the source's
  // Secret flag (applied later only if the "Copy Secret status" toggle is on).
  const linkSource = (targetIdx, source) => {
    setPreview((prev) => {
      const updated = { ...prev, unmatchedTarget: [...prev.unmatchedTarget] };
      const row = updated.unmatchedTarget[targetIdx];
      const existing = row.targetExistingTags || [];
      const merged = Array.from(new Set([...existing, ...(source.sourceTags || [])]));
      updated.unmatchedTarget[targetIdx] = {
        ...row,
        matchedSourceId: source.sourceId,
        matchedSourceName: source.sourceName,
        matchedSourceDescription: source.sourceDescription,
        matchedSourceTags: source.sourceTags || [],
        matchedSourceIsSecret: !!source.sourceIsSecret,
        pendingTags: merged,
        edited: true,
      };
      return updated;
    });
    setLinkPickerIdx(null);
    setEditingKey(null);
  };

  const unlinkSource = (targetIdx) => {
    setPreview((prev) => {
      const updated = { ...prev, unmatchedTarget: [...prev.unmatchedTarget] };
      const row = { ...updated.unmatchedTarget[targetIdx] };
      delete row.matchedSourceId;
      delete row.matchedSourceName;
      delete row.matchedSourceDescription;
      delete row.matchedSourceTags;
      delete row.matchedSourceIsSecret;
      row.pendingTags = row.targetExistingTags || [];
      row.edited = false;
      updated.unmatchedTarget[targetIdx] = row;
      return updated;
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const sourceId = String(active.id).replace("source-", "");
    const targetIdx = parseInt(String(over.id).replace("target-", ""), 10);
    if (Number.isNaN(targetIdx)) return;
    const source = (preview?.unmatchedSource || []).find((s) => s.sourceId === sourceId);
    if (source) linkSource(targetIdx, source);
  };

  const applyTags = async () => {
    setStep("applying");
    try {
      // Include a matched row when it has tags to write OR when we're also
      // copying secret status and the source differs from the target.
      const matchedItems = preview.matched
        .filter((m) => {
          const hasTags = m.mergedTags && m.mergedTags.length > 0;
          const secretChange = includeSecret && !!m.sourceIsSecret !== !!m.targetIsSecret;
          const dlcChange = includeDlc && !!m.sourceDlcName;
          return hasTags || secretChange || dlcChange;
        })
        .map((m) => {
          const item = {
            target_achievement_id: m.targetId,
            tags: m.mergedTags || [],
          };
          if (includeSecret) {
            item.isSecret = !!m.sourceIsSecret;
          }
          if (includeDlc && m.sourceDlcName) {
            item.sourceDlcName = m.sourceDlcName;
          }
          return item;
        });

      // Include unmatched-target rows the user manually edited or linked.
      const unmatchedItems = (preview.unmatchedTarget || [])
        .filter((u) => u.edited)
        .map((u) => {
          const item = {
            target_achievement_id: u.targetId,
            tags: u.pendingTags || [],
          };
          // For manually-linked rows, copy the source's Secret flag when the
          // "Copy Secret status" toggle is on and it differs from the target.
          if (
            includeSecret &&
            u.matchedSourceId != null &&
            !!u.matchedSourceIsSecret !== !!u.targetIsSecret
          ) {
            item.isSecret = !!u.matchedSourceIsSecret;
          }
          return item;
        });

      const items = [...matchedItems, ...unmatchedItems];
      const { data } = await axios.post(`${API}/master/games/${targetGameId}/copy-tags-apply`, {
        items,
        applyDlcAssignments: includeDlc,
      });
      setApplyResult(data);
      if (onApplied) onApplied();
    } catch (err) {
      alert("Failed to apply tags: " + (err.response?.data?.detail || err.message));
      setStep("preview");
    }
  };

  const editedUnmatchedCount = (preview?.unmatchedTarget || []).filter((u) => u.edited).length;
  const applyCount = (preview?.matched?.length || 0) + editedUnmatchedCount;

  // Group matched achievements by the DLC they'll be moved into (only when the
  // DLC toggle is on). Used to render a summary above the action buttons.
  const dlcSummary = includeDlc
    ? Object.entries(
        (preview?.matched || []).reduce((acc, m) => {
          if (m.sourceDlcName) {
            acc[m.sourceDlcName] = (acc[m.sourceDlcName] || 0) + 1;
          }
          return acc;
        }, {})
      ).sort((a, b) => a[0].localeCompare(b[0]))
    : [];
  const dlcTotal = dlcSummary.reduce((sum, [, n]) => sum + n, 0);

  // Source achievements not yet linked to a target — available to drag/link.
  const linkedSourceIds = new Set(
    (preview?.unmatchedTarget || []).map((u) => u.matchedSourceId).filter(Boolean)
  );
  const availableSources = (preview?.unmatchedSource || []).filter(
    (s) => !linkedSourceIds.has(s.sourceId)
  );

  const totalUnmatched =
    (preview?.unmatchedTarget?.length || 0) + (preview?.unmatchedSource?.length || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2">
            <Copy className="w-4 h-4 text-cyan-400" />
            Copy Tags from Another Game
          </DialogTitle>
          <DialogDescription className="text-white/40 text-xs">
            {step === "search" && `Select a source game to copy achievement tags to "${targetGameName}"`}
            {step === "preview" && `Review matched achievements and tags before applying`}
            {step === "applying" && (applyResult ? "Tags applied!" : "Applying tags...")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 pr-1 -mr-1">
          {/* STEP 1: Search & Select Source Game */}
          {step === "search" && (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-white/30" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for source game..."
                  className="pl-9 bg-white/5 border-white/10 text-white text-sm h-9"
                  data-testid="copy-tags-search"
                  autoFocus
                />
              </div>

              {searching && (
                <div className="flex items-center gap-2 text-white/40 text-xs py-4 justify-center">
                  <Loader2 className="w-3 h-3 animate-spin" /> Searching...
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="space-y-1 max-h-80 overflow-y-auto">
                  {searchResults.map((game) => {
                    // Extract release year from any standard date format.
                    const yr = (game.releaseDate || "").match(/(\d{4})/);
                    return (
                    <button
                      key={game.id}
                      onClick={() => loadPreview(game)}
                      disabled={loading}
                      className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-white/5 transition text-left group"
                      data-testid={`copy-tags-source-${game.id}`}
                    >
                      {game.boxArtUrl ? (
                        <img src={game.boxArtUrl} alt="" className="w-8 h-8 rounded object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded bg-white/5 flex items-center justify-center text-white/20 text-[8px]">?</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-xs text-white truncate">{game.name}</p>
                          {yr && (
                            <span
                              className="text-[9px] uppercase tracking-wide px-1 py-0.5 rounded bg-white/10 text-white/70 border border-white/10 font-semibold flex-shrink-0"
                              title={`Released ${game.releaseDate}`}
                            >
                              {yr[1]}
                            </span>
                          )}
                          {game.region && (
                            <span
                              className="text-[9px] uppercase tracking-wide px-1 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-400/30 font-semibold flex-shrink-0"
                              title={`Region: ${game.region}`}
                            >
                              {game.region}
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-white/30 truncate">
                          {[
                            game.platforms?.join(", "),
                            `${game.totalAchievements || 0} achievements`,
                            game.developer || (game.developers && game.developers[0]),
                          ]
                            .filter((s) => s && String(s).trim())
                            .join(" — ")}
                        </p>
                      </div>
                      {loading && selectedSource?.id === game.id ? (
                        <Loader2 className="w-3 h-3 animate-spin text-cyan-400" />
                      ) : (
                        <ChevronDown className="w-3 h-3 text-white/20 group-hover:text-cyan-400 rotate-[-90deg]" />
                      )}
                    </button>
                    );
                  })}
                </div>
              )}

              {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
                <p className="text-white/30 text-xs text-center py-4">No games found</p>
              )}
            </div>
          )}

          {/* STEP 2: Preview Matches */}
          {step === "preview" && preview && (
            <div className="space-y-3">
              {/* Summary bar */}
              <div className="flex items-center gap-3 bg-white/5 rounded-md p-2 text-xs">
                <span className="text-white/50">Source:</span>
                <span className="text-cyan-400 font-medium">{preview.sourceGame}</span>
                <span className="text-white/20 mx-1">→</span>
                <span className="text-white/50">Target:</span>
                <span className="text-amber-400 font-medium">{preview.targetGame}</span>
              </div>

              <div className="flex items-center gap-4 text-xs text-white/40">
                <span className="text-green-400">{preview.summary.matched} matched</span>
                {totalUnmatched > 0 && (
                  <button
                    onClick={() => setShowUnmatched(!showUnmatched)}
                    className="flex items-center gap-1 text-amber-400 hover:text-amber-300"
                  >
                    <AlertTriangle className="w-3 h-3" />
                    {totalUnmatched} unmatched
                    {showUnmatched ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                )}
              </div>

              {/* Unmatched section (collapsible) */}
              {showUnmatched && totalUnmatched > 0 && (
                <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                  <div className="bg-amber-400/5 border border-amber-400/20 rounded-md p-2">
                    <p className="text-[10px] text-amber-400/70 mb-2">
                      Drag a source achievement onto a target — or use its <span className="text-cyan-300">Link</span> button —
                      to manually match mismatched titles. Source tags merge into the target and the Secret flag is copied
                      (when the toggle below is on). You can still edit tags before applying.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* LEFT: Unmatched target achievements (drop zones) */}
                      <div className="space-y-1">
                        <p className="text-[10px] text-amber-400/70 uppercase tracking-wider mb-1">
                          Target — no source match ({preview.unmatchedTarget?.length || 0})
                        </p>
                        {(preview.unmatchedTarget || []).length === 0 && (
                          <p className="text-white/20 text-[10px] py-1">None</p>
                        )}
                        {(preview.unmatchedTarget || []).map((u, i) => {
                          const key = `unmatched-${i}`;
                          const isEditing = editingKey === key;
                          const currentTags = u.pendingTags ?? u.targetExistingTags ?? [];
                          return (
                            <DroppableTarget key={u.targetId} idx={i} linked={!!u.matchedSourceId}>
                              {(isOver) => (
                                <div
                                  className={`rounded-md p-2 text-xs transition ${
                                    isOver
                                      ? "bg-cyan-400/15 border border-dashed border-cyan-400/60"
                                      : isEditing
                                      ? "bg-cyan-400/10 border border-cyan-400/20"
                                      : u.matchedSourceId
                                      ? "bg-green-400/[0.06] border border-green-400/25"
                                      : "bg-white/[0.02] border border-transparent hover:bg-white/5"
                                  }`}
                                  data-testid={`copy-tags-unmatched-row-${i}`}
                                >
                                  <div className="flex items-start gap-2">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        <p className="text-white/80 font-medium truncate">{u.targetName}</p>
                                        {u.targetIsSecret && (
                                          <span className="text-[9px] px-1 py-0 rounded bg-amber-500/15 text-amber-300/80 border border-amber-400/20 font-semibold uppercase tracking-wide" title="Currently Secret">
                                            Secret
                                          </span>
                                        )}
                                        {u.matchedSourceId ? (
                                          <span className="text-[9px] px-1 py-0 rounded bg-green-500/15 text-green-300 border border-green-400/30 font-semibold uppercase tracking-wide">
                                            Linked
                                          </span>
                                        ) : u.edited ? (
                                          <span className="text-[9px] px-1 py-0 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-400/30 font-semibold uppercase tracking-wide">
                                            Edited
                                          </span>
                                        ) : null}
                                      </div>
                                      <p className="text-white/30 text-[10px] truncate mt-0.5">{u.targetDescription}</p>
                                      {!u.matchedSourceId && (
                                        <p className="text-amber-400/60 text-[9px] mt-0.5">{u.reason}</p>
                                      )}
                                      {u.matchedSourceId && (
                                        <p className="text-green-400/70 text-[9px] mt-0.5 truncate">
                                          Matched to source: &quot;{u.matchedSourceName}&quot;
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      {u.matchedSourceId ? (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => unlinkSource(i)}
                                          className="h-6 px-2 text-[10px] text-white/50 hover:text-red-300"
                                          data-testid={`copy-tags-unlink-${i}`}
                                        >
                                          <Link2Off className="w-3 h-3 mr-1" /> Unlink
                                        </Button>
                                      ) : (
                                        availableSources.length > 0 && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setLinkPickerIdx(linkPickerIdx === i ? null : i)}
                                            className="h-6 px-2 text-[10px] shrink-0"
                                            data-testid={`copy-tags-link-btn-${i}`}
                                          >
                                            <Link2 className="w-3 h-3 mr-1" /> Link
                                          </Button>
                                        )
                                      )}
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => isEditing ? saveEditRow() : startEditRow("unmatched", i)}
                                        className="h-6 px-2 text-[10px] shrink-0"
                                        data-testid={`copy-tags-unmatched-edit-${i}`}
                                      >
                                        {isEditing ? (
                                          <><Check className="w-3 h-3 mr-1" /> Done</>
                                        ) : (
                                          <><Tag className="w-3 h-3 mr-1" /> Edit</>
                                        )}
                                      </Button>
                                    </div>
                                  </div>

                                  {/* Link picker dropdown (click/select fallback) */}
                                  {linkPickerIdx === i && !u.matchedSourceId && availableSources.length > 0 && (
                                    <div className="mt-2 border border-cyan-400/20 rounded-md bg-black/40 max-h-40 overflow-y-auto divide-y divide-white/5" data-testid={`copy-tags-link-picker-${i}`}>
                                      {availableSources.map((s) => (
                                        <button
                                          key={s.sourceId}
                                          onClick={() => linkSource(i, s)}
                                          className="w-full text-left px-2 py-1.5 hover:bg-cyan-400/10 transition"
                                          data-testid={`copy-tags-link-option-${i}-${s.sourceId}`}
                                        >
                                          <p className="text-white/80 text-[11px] truncate">{s.sourceName}</p>
                                          {s.sourceDescription && (
                                            <p className="text-white/30 text-[9px] truncate">{s.sourceDescription}</p>
                                          )}
                                        </button>
                                      ))}
                                    </div>
                                  )}

                                  {isEditing ? (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                      {(allTags || []).map((tag) => (
                                        <button
                                          key={tag.id}
                                          onClick={() => toggleEditTag(tag.id)}
                                          className={`px-1.5 py-0.5 rounded text-[9px] border transition ${
                                            editTags.includes(tag.id)
                                              ? "bg-cyan-400/20 border-cyan-400/40 text-cyan-300"
                                              : "bg-white/5 border-white/10 text-white/30 hover:text-white/50"
                                          }`}
                                        >
                                          {tag.name}
                                        </button>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      {currentTags.map((tagId) => {
                                        const tag = tagMap[tagId];
                                        const isNew = u.edited && !(u.targetExistingTags || []).includes(tagId);
                                        return (
                                          <Badge
                                            key={tagId}
                                            variant="outline"
                                            className={`text-[9px] px-1 py-0 h-4 ${
                                              isNew
                                                ? "border-green-400/40 text-green-400/80 bg-green-400/10"
                                                : "border-white/10 text-white/40"
                                            }`}
                                          >
                                            {isNew && <span className="mr-0.5">+</span>}
                                            {tag?.name || tagId}
                                          </Badge>
                                        );
                                      })}
                                      {currentTags.length === 0 && (
                                        <span className="text-white/20 text-[10px]">No tags</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </DroppableTarget>
                          );
                        })}
                      </div>

                      {/* RIGHT: Unmatched source achievements (draggable) */}
                      <div className="space-y-1">
                        <p className="text-[10px] text-amber-400/70 uppercase tracking-wider mb-1">
                          Source — not in target ({availableSources.length})
                        </p>
                        {availableSources.length === 0 && (
                          <p className="text-white/20 text-[10px] py-1">
                            {preview.unmatchedSource?.length > 0 ? "All linked" : "None"}
                          </p>
                        )}
                        {availableSources.map((s) => (
                          <DraggableSource
                            key={s.sourceId}
                            source={s}
                            tagMap={tagMap}
                            onPick={(src) => {
                              // Link to first still-unmatched target, or the one
                              // whose picker is open.
                              const targetIdx =
                                linkPickerIdx != null && !preview.unmatchedTarget[linkPickerIdx]?.matchedSourceId
                                  ? linkPickerIdx
                                  : (preview.unmatchedTarget || []).findIndex((t) => !t.matchedSourceId);
                              if (targetIdx >= 0) linkSource(targetIdx, src);
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </DndContext>
              )}

              {/* Matched achievements table */}
              <div className="flex items-center justify-between text-[11px] text-white/50 px-1 pt-1">
                <span>
                  {preview.matched.length} matched · click &quot;Edit&quot; on any row to adjust tags
                </span>
                <label className="inline-flex items-center gap-1.5 cursor-pointer select-none" data-testid="copy-tags-secret-toggle-wrap">
                  <input
                    type="checkbox"
                    className="accent-cyan-400 w-3.5 h-3.5"
                    checked={includeSecret}
                    onChange={(e) => setIncludeSecret(e.target.checked)}
                    data-testid="copy-tags-include-secret"
                  />
                  <span className="text-white/70">Copy Secret status from source</span>
                </label>
                <label className="inline-flex items-center gap-1.5 cursor-pointer select-none" data-testid="copy-tags-dlc-toggle-wrap">
                  <input
                    type="checkbox"
                    className="accent-cyan-400 w-3.5 h-3.5"
                    checked={includeDlc}
                    onChange={(e) => setIncludeDlc(e.target.checked)}
                    data-testid="copy-tags-include-dlc"
                  />
                  <span className="text-white/70">Also match DLC assignments</span>
                </label>
              </div>
              <div className="space-y-0.5 max-h-[50vh] overflow-y-auto">
                {preview.matched.map((m, idx) => {
                  const key = `matched-${idx}`;
                  const isEditing = editingKey === key;
                  // A row is "DLC-bound" when the DLC toggle is on and the source
                  // achievement carries a DLC name — these get a cyan glow.
                  const dlcBound = includeDlc && !!m.sourceDlcName;
                  return (
                  <div
                    key={m.targetId}
                    className={`rounded-md p-2 text-xs transition ${
                      isEditing
                        ? "bg-cyan-400/10 border border-cyan-400/20"
                        : dlcBound
                        ? "bg-cyan-400/[0.07] border border-cyan-400/40 ring-1 ring-cyan-400/40 shadow-[0_0_14px_-2px_rgba(34,211,238,0.45)]"
                        : "bg-white/[0.02] border border-transparent hover:bg-white/5"
                    }`}
                    data-testid={`copy-tags-row-${idx}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-white/80 font-medium truncate">{m.targetName}</p>
                          {/* Secret indicators — show what's currently on target and what will change. */}
                          {m.targetIsSecret && (
                            <span className="text-[9px] px-1 py-0 rounded bg-amber-500/15 text-amber-300/80 border border-amber-400/20 font-semibold uppercase tracking-wide" title="Currently Secret on target">
                              Secret
                            </span>
                          )}
                          {includeSecret && !!m.sourceIsSecret !== !!m.targetIsSecret && (
                            <span
                              className={`text-[9px] px-1 py-0 rounded font-semibold uppercase tracking-wide border ${
                                m.sourceIsSecret
                                  ? "bg-amber-400/15 text-amber-300 border-amber-400/40"
                                  : "bg-white/5 text-white/40 border-white/10"
                              }`}
                              title={m.sourceIsSecret ? "Will become Secret" : "Will become NOT Secret"}
                            >
                              {m.sourceIsSecret ? "+ Secret" : "− Secret"}
                            </span>
                          )}
                          {dlcBound && (
                            <span
                              className="text-[9px] px-1 py-0 rounded font-semibold tracking-wide border bg-cyan-400/15 text-cyan-300 border-cyan-400/40 inline-flex items-center gap-0.5"
                              title={`Will be assigned to DLC: ${m.sourceDlcName}`}
                              data-testid={`copy-tags-dlc-badge-name-${idx}`}
                            >
                              → DLC: {m.sourceDlcName}
                            </span>
                          )}
                        </div>
                        <p className="text-white/30 text-[10px] truncate mt-0.5">{m.targetDescription}</p>
                        {!m.isExactNameMatch && (
                          <p className="text-amber-400/60 text-[9px] mt-0.5">
                            Fuzzy match → source: &quot;{m.sourceName}&quot;
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => isEditing ? saveEditRow() : startEditRow("matched", idx)}
                        className="h-6 px-2 text-[10px] shrink-0"
                        data-testid={`copy-tags-edit-${idx}`}
                      >
                        {isEditing ? (
                          <><Check className="w-3 h-3 mr-1" /> Done</>
                        ) : (
                          <><Tag className="w-3 h-3 mr-1" /> Edit</>
                        )}
                      </Button>
                    </div>

                    {/* Tags display / editor */}
                    {isEditing ? (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(allTags || []).map((tag) => (
                          <button
                            key={tag.id}
                            onClick={() => toggleEditTag(tag.id)}
                            className={`px-1.5 py-0.5 rounded text-[9px] border transition ${
                              editTags.includes(tag.id)
                                ? "bg-cyan-400/20 border-cyan-400/40 text-cyan-300"
                                : "bg-white/5 border-white/10 text-white/30 hover:text-white/50"
                            }`}
                          >
                            {tag.name}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-1 flex flex-wrap gap-1 items-center">
                        {dlcBound && (
                          <Badge
                            variant="outline"
                            className="text-[9px] px-1 py-0 h-4 border-cyan-400/40 text-cyan-300 bg-cyan-400/10 font-semibold"
                            title={`Will be assigned to DLC: ${m.sourceDlcName}`}
                            data-testid={`copy-tags-dlc-badge-tags-${idx}`}
                          >
                            → DLC: {m.sourceDlcName}
                          </Badge>
                        )}
                        {m.mergedTags?.map((tagId) => {
                          const tag = tagMap[tagId];
                          const isNew = !m.targetExistingTags?.includes(tagId);
                          return (
                            <Badge
                              key={tagId}
                              variant="outline"
                              className={`text-[9px] px-1 py-0 h-4 ${
                                isNew
                                  ? "border-green-400/40 text-green-400/80 bg-green-400/10"
                                  : "border-white/10 text-white/40"
                              }`}
                            >
                              {isNew && <span className="mr-0.5">+</span>}
                              {tag?.name || tagId}
                            </Badge>
                          );
                        })}
                        {(!m.mergedTags || m.mergedTags.length === 0) && (
                          <span className="text-white/20 text-[10px]">No tags</span>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>

              {/* DLC assignment summary */}
              {includeDlc && dlcSummary.length > 0 && (
                <div
                  className="rounded-md border border-cyan-400/30 bg-cyan-400/[0.06] p-2.5 space-y-1.5"
                  data-testid="copy-tags-dlc-summary"
                >
                  <p className="text-[11px] font-semibold text-cyan-300 flex items-center gap-1.5">
                    <Link2 className="w-3 h-3" />
                    {dlcTotal} achievement{dlcTotal === 1 ? "" : "s"} will move into {dlcSummary.length} DLC{dlcSummary.length === 1 ? "" : "s"}
                  </p>
                  <div className="space-y-1">
                    {dlcSummary.map(([name, count]) => (
                      <div
                        key={name}
                        className="flex items-center justify-between gap-2 text-[11px]"
                        data-testid={`copy-tags-dlc-summary-row-${name}`}
                      >
                        <span className="text-white/70 truncate">{name}</span>
                        <span className="shrink-0 text-cyan-300 font-medium">
                          {count} achievement{count === 1 ? "" : "s"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex items-center justify-between pt-2 border-t border-white/5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setStep("search"); setPreview(null); setSelectedSource(null); }}
                  className="text-xs h-7"
                >
                  Back to Search
                </Button>
                <Button
                  size="sm"
                  onClick={applyTags}
                  disabled={applyCount === 0}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white text-xs h-7 px-4"
                  data-testid="copy-tags-apply-btn"
                >
                  Apply Tags to {applyCount} Achievement{applyCount === 1 ? "" : "s"}
                  {editedUnmatchedCount > 0 && (
                    <span className="ml-1 text-cyan-200/80">
                      ({preview.matched.length} matched + {editedUnmatchedCount} manual)
                    </span>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3: Applying / Done */}
          {step === "applying" && (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              {applyResult ? (
                <>
                  <Check className="w-8 h-8 text-green-400" />
                  <p className="text-sm text-white/70">
                    Updated tags on <span className="text-green-400 font-medium">{applyResult.updated}</span> achievements
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    className="mt-2 border-white/20 text-xs"
                  >
                    Close
                  </Button>
                </>
              ) : (
                <>
                  <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
                  <p className="text-xs text-white/40">Applying tags...</p>
                </>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
